// OWNER: Agent C. Shared add/edit loan form — used by the Loans page ("Add
// loan") and the Admin section ("Adjust loan"), which reuses it in edit mode.
import { useState } from "react";
import { Dialog } from "@cloudflare/kumo/components/dialog";
import { Input } from "@cloudflare/kumo/components/input";
import { Select } from "@cloudflare/kumo/components/select";
import { Text } from "@cloudflare/kumo/components/text";
import { Button } from "@cloudflare/kumo/components/button";
import type { Loan } from "@shared/types";
import { createLoan, updateLoan, type CreateLoanBody } from "../../lib/hooks";
import { parseAmountInput } from "../../lib/numberInput";

// Loans are only ever INR or GBP per the API contract — no USD loans.
type LoanCurrency = "INR" | "GBP";
const CURRENCY_ITEMS: Record<LoanCurrency, string> = { INR: "₹ INR", GBP: "£ GBP" };

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export type LoanFormState = { mode: "create" } | { mode: "edit"; loan: Loan } | null;

export function LoanFormDialog({
  state,
  onClose,
  onSaved,
}: {
  state: LoanFormState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = state?.mode === "edit" ? state.loan : null;
  // These initializers only read `editing` once, at mount — the caller must
  // key <LoanFormDialog> per distinct target (see AccountFormDialog's own
  // comment on the same pattern) so switching targets forces a remount.
  const [name, setName] = useState(editing?.name ?? "");
  const [lender, setLender] = useState(editing?.lender ?? "");
  const [currency, setCurrency] = useState<LoanCurrency>((editing?.currency as LoanCurrency) ?? "INR");
  const [principal, setPrincipal] = useState(editing ? String(editing.principal) : "");
  const [outstandingPrincipal, setOutstandingPrincipal] = useState(editing ? String(editing.outstandingPrincipal) : "");
  const [interestRate, setInterestRate] = useState(editing?.interestRate != null ? String(editing.interestRate) : "");
  const [emiAmount, setEmiAmount] = useState(editing?.emiAmount != null ? String(editing.emiAmount) : "");
  const [tenureMonths, setTenureMonths] = useState(editing?.tenureMonths != null ? String(editing.tenureMonths) : "");
  const [startDate, setStartDate] = useState(editing?.startDate ?? today());
  const [nextDueDate, setNextDueDate] = useState(editing?.nextDueDate ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = state != null;
  const openKey = `${open}-${editing?.id ?? "new"}`;

  function handleOpenChange(next: boolean) {
    if (!next) onClose();
  }

  async function handleSubmit() {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    // Amount fields accept comma thousands-separators for readability
    // ("12,50,000") — strip and validate rather than saving NaN.
    const parsedPrincipal = parseAmountInput(principal);
    if (parsedPrincipal === null || parsedPrincipal <= 0) {
      setError("Principal must be a valid amount greater than zero");
      return;
    }
    const parsedOutstanding = outstandingPrincipal.trim() ? parseAmountInput(outstandingPrincipal) : parsedPrincipal;
    if (parsedOutstanding === null || parsedOutstanding < 0) {
      setError("Outstanding principal must be a valid amount");
      return;
    }
    const parsedInterestRate = interestRate.trim() ? parseAmountInput(interestRate) : null;
    if (interestRate.trim() && parsedInterestRate === null) {
      setError("Interest rate must be a valid number");
      return;
    }
    const parsedEmiAmount = emiAmount.trim() ? parseAmountInput(emiAmount) : null;
    if (emiAmount.trim() && parsedEmiAmount === null) {
      setError("EMI amount must be a valid number");
      return;
    }
    const parsedTenureMonths = tenureMonths.trim() ? parseAmountInput(tenureMonths) : null;
    if (tenureMonths.trim() && (parsedTenureMonths === null || !Number.isInteger(parsedTenureMonths))) {
      setError("Tenure must be a whole number of months");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const body: CreateLoanBody = {
        name: name.trim(),
        lender: lender.trim() || null,
        currency,
        principal: parsedPrincipal,
        outstandingPrincipal: parsedOutstanding,
        interestRate: parsedInterestRate,
        emiAmount: parsedEmiAmount,
        tenureMonths: parsedTenureMonths,
        startDate: startDate || null,
        nextDueDate: nextDueDate || null,
      };
      if (editing) await updateLoan(editing.id, body);
      else await createLoan(body);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root key={openKey} open={open} onOpenChange={handleOpenChange}>
      <Dialog className="max-h-[85vh] overflow-y-auto p-6">
        <Dialog.Title className="text-lg font-semibold">{editing ? "Adjust loan" : "Add loan"}</Dialog.Title>
        <div className="mt-4 space-y-4">
          <Input label="Name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Home loan" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Lender" required={false} value={lender} onChange={(e) => setLender(e.target.value)} placeholder="Optional — e.g. HDFC" />
            <Select label="Currency" value={currency} onValueChange={(v) => setCurrency((v as LoanCurrency) ?? "INR")} items={CURRENCY_ITEMS} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Principal"
              type="text"
              inputMode="decimal"
              required
              placeholder="0.00"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
            />
            <Input
              label="Outstanding principal"
              required={false}
              type="text"
              inputMode="decimal"
              placeholder="Defaults to principal"
              value={outstandingPrincipal}
              onChange={(e) => setOutstandingPrincipal(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Interest rate"
              required={false}
              type="text"
              inputMode="decimal"
              placeholder="Annual %, optional"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
            />
            <Input
              label="EMI amount"
              required={false}
              type="text"
              inputMode="decimal"
              placeholder="Optional"
              value={emiAmount}
              onChange={(e) => setEmiAmount(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Tenure (months)"
              required={false}
              type="text"
              inputMode="numeric"
              placeholder="Optional"
              value={tenureMonths}
              onChange={(e) => setTenureMonths(e.target.value)}
            />
            <Input label="Start date" required={false} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <Input label="Next due date" required={false} type="date" value={nextDueDate} onChange={(e) => setNextDueDate(e.target.value)} />
        </div>
        {error && (
          <div className="mt-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>
            {editing ? "Save changes" : "Add loan"}
          </Button>
        </div>
      </Dialog>
    </Dialog.Root>
  );
}
